<script setup lang="ts">
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';

export interface DataItem {
  id: number;
  jobId: number;
  jobDate: string;
  clientIcon: string | null | undefined;
  clientIconTooltip: string | null | undefined;
  clientCode: string;
  clientName: string;
  from: string;
  to: string;
  service: string;
  notes: string;
}

export interface Props {
  data?: DataItem[];
}
const props = withDefaults(defineProps<Props>(), {
  data: () => [],
});

const titleData = {
  jobId: 'Job ID',
  jobDate: 'Date',
  client: 'Client',
  from: 'From',
  to: 'To',
  service: 'SVC',
  notes: 'Notes',
};

const foldColspan = Object.keys(titleData).length;
</script>

<template>
  <table
    data-testid="dispatchDriverJobsCustomTable"
    :class="$style.dispatchDriverJobsCustomTable"
  >
    <thead>
      <tr>
        <template
          v-for="(titleValue, titleKey, titleIndex) in titleData"
          :key="titleKey"
        >
          <th :class="$style[titleKey + 'Th']">
            {{ titleValue }}
          </th>
        </template>
      </tr>
    </thead>
    <tr v-if="!props.data?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText :class="$style.tableEmptyText">
          Driver doesn’t have any jobs
        </TableEmptyText>
      </td>
    </tr>
    <tbody v-else>
      <template v-for="(dataItem, dataIndex) in props.data" :key="dataItem.id">
        <tr>
          <td :class="$style.jobIdTd">
            <JobIdAndIconLinkUnit
              :job-id="dataItem.jobId"
              :job-date="dataItem.jobDate"
            />
          </td>
          <td :class="$style.jobDateTd">
            {{ dataItem.jobDate }}
          </td>
          <td :class="$style.clientTd">
            <span>
              <KParentTooltip location="top">
                {{ dataItem.clientName }}
              </KParentTooltip>
              {{ dataItem.clientCode }}
            </span>
            <sup v-if="dataItem.clientIcon" :class="$style.sup">
              {{ dataItem.clientIcon }}
              <KParentTooltip location="right">
                {{ dataItem.clientIconTooltip }}
              </KParentTooltip>
            </sup>
          </td>
          <td :class="$style.fromTd">
            <span>
              <KParentTooltip location="top">
                {{ dataItem.from }}
              </KParentTooltip>
              {{ dataItem.from }}
            </span>
          </td>
          <td :class="$style.toTd">
            <span>
              <KParentTooltip location="top">
                {{ dataItem.to }}
              </KParentTooltip>
              {{ dataItem.to }}
            </span>
          </td>
          <td :class="$style.serviceTd">
            {{ dataItem.service }}
          </td>
          <td :class="$style.notesTd">
            {{ dataItem.notes }}
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchDriverJobsCustomTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
  line-height: 1.2;
  text-align: left;

  & > thead > tr,
  & > tbody > tr,
  & > tr {
    & > th {
      font-weight: 500;
      padding: 4px 6px;
      vertical-align: baseline;
    }
    & > td {
      padding: 4px 6px;
      vertical-align: baseline;
    }
    & > th:first-child,
    & > td:first-child {
      padding-left: 0;
    }
    & > th:last-child,
    & > td:last-child {
      padding-right: 0;
    }
  }
}
  .jobIdTh {
    min-width: 75px;
    width: 75px;
  }
  .jobDateTh {
    min-width: 80px;
    width: 80px;
  }
  .clientTh {
  }
  .fromTh {
  }
  .toTh {
  }
  .serviceTh {
  }
  .notesTh {
  }
  .jobIdTd {
  }
  .jobDateTd {
    white-space: nowrap;
  }
  .clientTd {
    white-space: nowrap;
  }
    .sup {
      font-size: 80%;
      position: relative;
      top: -6px;
    }
  .fromTd {
    word-break: break-word;
  }
  .toTd {
    word-break: break-word;
  }
  .serviceTd {
  }
  .notesTd {
  }

.tableEmptyText {
  height: 24.5px;

  :global {
    p {
      margin: 0;
    }
  }
}
</style>
